import React, { Fragment } from 'react';
import { useTranslation } from 'react-i18next';

import { useCurrentUser } from '@klp/auth';
import type { IByggInfo } from '@klp/bygg/classes';
import { BuildingTabs } from '@klp/bygg/classes';
import {
    ByggCalendarAccess,
    ByggRoutes,
    FakturaLinkAccess,
} from '@klp/bygg/config';
import { Conditional } from '@klp/conditional';
import { Block, Link } from '@klp/design';
import { useKlpStyles } from '@klp/design/Theme';
import {
    MiljoprofilAccess,
    MiljoprofilRoutes,
} from '@klp/sustainability/config';

import { ByggInfoSummaryCalendar } from './ByggInfoSummaryCalendar';

const styles = () => ({
    title: {
        textTransform: 'uppercase',
    },
});

interface IProps {
    byggInfo: Array<IByggInfo>;
    avtaler: number;
}

export const ByggInfoSummary: React.FC<IProps> = ({ byggInfo, avtaler }) => {
    const { t } = useTranslation();
    const classes = useKlpStyles({}, styles);
    const { currentUser: user } = useCurrentUser();

    const hasByggInfo = Array.isArray(byggInfo) && byggInfo.length > 0;
    const hasAvtaler = typeof avtaler === 'number' && avtaler >= 0;

    return (
        <>
            <Conditional condition={hasByggInfo || hasAvtaler}>
                <Block
                    marginTop="compact"
                    klpTypographyVariant="xsmall"
                    className={classes.title}
                    data-testid="ByggInfoSummary__AvtalerTitle"
                >
                    {t('kontrakt.agreements')}
                </Block>

                <Conditional condition={hasByggInfo}>
                    <Block
                        translation="bygg.byggInfo.numberOfBuildings"
                        translationOptions={{
                            count: byggInfo ? byggInfo.length : 0,
                        }}
                        klpTypographyVariant="small"
                        data-testid="ByggInfoSummary__NumberOfBuildings"
                    />
                </Conditional>

                <Conditional condition={hasAvtaler}>
                    <Block
                        translation="bygg.byggInfo.numberOfContracts"
                        translationOptions={{ count: avtaler }}
                        klpTypographyVariant="small"
                        data-testid="ByggInfoSummary__NumberOfContracts"
                    />
                </Conditional>
            </Conditional>

            <Conditional condition={hasByggInfo}>
                {byggInfo && byggInfo[0] && (
                    <Fragment>
                        <Block
                            marginTop="compact"
                            klpTypographyVariant="xsmall"
                            className={classes.title}
                            data-testid="ByggInfoSummary__SnarveiTitle"
                        >
                            {t('bygg.shortcut')}
                        </Block>
                        <Block>
                            <Block
                                klpTypographyVariant="small"
                                data-testid="ByggInfoSummary__BuildingName"
                            >
                                {byggInfo[0].navn}
                            </Block>

                            <Block>
                                <Link
                                    translation="bygg.documents"
                                    href={ByggRoutes.single(
                                        byggInfo[0].byggNummer,
                                        BuildingTabs.documents,
                                    )}
                                    klpTypographyVariant="xsmall"
                                    data-testid="ByggInfoSummary__BuildingDocuments"
                                />
                            </Block>
                            {FakturaLinkAccess(user) && (
                                <Block>
                                    <Link
                                        translation="faktura.faktura"
                                        href={ByggRoutes.single(
                                            byggInfo[0].byggNummer,
                                            BuildingTabs.invoices,
                                        )}
                                        klpTypographyVariant="xsmall"
                                        data-testid="ByggInfoSummary__BuildingInvoices"
                                    />
                                </Block>
                            )}
                            {MiljoprofilAccess() && (
                                <Block>
                                    <Link
                                        translation="bygg.forbruk"
                                        href={MiljoprofilRoutes.list(
                                            byggInfo[0].byggNummer,
                                        )}
                                        klpTypographyVariant="xsmall"
                                        data-testid="ByggInfoSummary__Miljoprofil"
                                    />
                                </Block>
                            )}
                            {ByggCalendarAccess(user) && (
                                <ByggInfoSummaryCalendar
                                    byggNummer={byggInfo[0].byggNummer}
                                />
                            )}
                        </Block>
                    </Fragment>
                )}
            </Conditional>
        </>
    );
};
